<script setup lang="ts">
import { InputType } from '~/static/enums/components/base-input'

interface Props {
  type?: InputType
  value: string | number | null
  placeholder?: string
  disabled?: boolean
  errorMessage?: string
  widthInput?: string
  enterkeyhint?: string
}

const props = withDefaults(defineProps<Props>(), {
  id: '',
  name: '',
  errorMessage: '',
  type: InputType.Text,
  placeholder: '',
  disabled: false,
  widthInput: '',
  enterkeyhint: 'done',
})
const emit = defineEmits<{
  (e: 'input', val: string): void
  (e: 'change', val: string): void
  (e: 'enter', val: string): void
  (e: 'click'): void
}>()

const hasError = computed<boolean>(() => {
  return props.errorMessage !== undefined && props.errorMessage.length > 0
})

const inputKey = ref<number>(0)
const updateInput = () => {
  inputKey.value++
}

const handleInput = (event: Event) => {
  emit('input', (event.target as HTMLInputElement).value)
}
const handleChange = (event: Event) => {
  const value = (event.target as HTMLInputElement).value
  if (value !== (props.value ?? '').toString()) {
    emit('change', value)
    updateInput()
  }
}
const handleEnter = (event: Event) => {
  const value = (event.target as HTMLInputElement).value
  if (value !== (props.value ?? '').toString()) {
    emit('change', value)
  }
  emit('enter', value)
  updateInput()
}

const clickEmit = () => {
  emit('click')
}

const inputRef: Ref = ref(null)
const setFocus = () => {
  if (inputRef.value !== null) {
    (<HTMLInputElement>inputRef.value).focus()
  }
}

defineExpose({
  setFocus,
})

</script>

<template>
  <div
    class="input-group prevent_unselection"
    @click="clickEmit"
  >
    <div
      v-if="$slots['left']"
      class="form-control w-fit-content d-flex align-items-center"
    >
      <slot name="left" />
    </div>
    <input
      :style="{'max-width': widthInput}"
      :value="value"
      :type="type"
      :key="inputKey"
      :placeholder="placeholder"
      :disabled="disabled"
      :class="{ 'is-invalid': hasError }"
      :enterkeyhint="enterkeyhint"
      @change="handleChange"
      @keyup.enter="handleEnter"
      @input="handleInput"
      class="form-control m-input"
      ref="inputRef"
    >
    <div
      v-if="hasError"
      id="id"
      class="invalid-feedback"
    >
      {{ errorMessage }}
    </div>
    <div
      v-if="$slots['right']"
      class="form-control w-fit-content d-flex align-items-center"
    >
      <slot name="right" />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.w-fit-content {
  max-width: fit-content;
}

.form-control {
  @include media-breakpoint-down(lg) {
    font-size: rem(14px);
    line-height: 150%;
    padding: rem(4px) rem(8px);
  }
}

.m-input {
  padding-top: rem(4px);
  padding-bottom: rem(8px);
}
</style>
